import type {FC} from 'react';

import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import {RadioTileFrame} from '../form/RadioTileFrame';
import {FormattedItemPrice} from '../item/FormattedItemPrice';

import type {ProductMaterialConfiguration} from './configuration';
import type {ProductMaterialOptionCoreFragment} from './query';

type ProductMaterialOptionSelectorProps = {
	namePrefix: string;
	options: ProductMaterialOptionCoreFragment[];
	defaultSelection?: ProductMaterialConfiguration;
};

export const ProductMaterialOptionSelector: FC<ProductMaterialOptionSelectorProps> = ({
	namePrefix,
	options,
	defaultSelection,
}) => {
	return (
		<>
			<div className="flex flex-col gap-4">
				{options.map((option) => {
					const material = option.material;

					if (!material || !material.colors) {
						return null;
					}

					return (
						<fieldset key={material._id}>
							<Paragraph className="mb-2 font-semibold" asChild>
								<legend>
									{option.material?.name}
									{option.items && option.items.length && (
										<>
											{' '}
											(+ <FormattedItemPrice items={option.items} />)
										</>
									)}
								</legend>
							</Paragraph>
							<div className="flex flex-wrap gap-2">
								{option.material?.colors?.map((materialColor) => {
									const image = imageFromSanityImage(
										materialColor.previewImage ?? material.previewImage,
										1,
									);
									if (!image) {
										return null;
									}

									return (
										<RadioTileFrame
											key={materialColor._key}
											name={`${namePrefix}.materialOptionAndColor`}
											value={`${option._key}:${materialColor._key}`}
											defaultChecked={
												option._key === defaultSelection?.optionKey &&
												materialColor._key === defaultSelection.colorKey
											}
										>
											<ImageBlock
												{...image}
												alt={materialColor?.color?.name || ''}
												className="rounded-xs object-contain"
												style={{width: 44, height: 44}}
											/>
										</RadioTileFrame>
									);
								})}
							</div>
						</fieldset>
					);
				})}
			</div>
		</>
	);
};
